import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Heart, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * StaySurveyModal — appears once at day 30, 90, and 180 milestones.
 * One question, multi-select tags + free text. Super-short, pure positivity.
 *
 * Triggered by `GET /feedback/pending-stay-survey` returning a milestone.
 * Dismisses are recorded so we don't re-ask the same milestone.
 */
export const StaySurveyModal = () => {
  const [milestone, setMilestone] = useState(null);
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState([]);
  const [freeText, setFreeText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/feedback/pending-stay-survey`, {
          credentials: "include",
        });
        if (!r.ok || cancelled) return;
        const d = await r.json();
        if (d.milestone) {
          setMilestone(d.milestone);
          setOptions(d.options || {});
          setOpen(true);
        }
      } catch {
        // silent — survey is non-critical
      }
    };
    // Small delay so we don't race the user's initial load
    const t = setTimeout(load, 2500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, []);

  if (!open || !milestone) return null;

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleClose = async () => {
    if (submitting) return;
    setOpen(false);
    // Record dismiss so we don't re-ask this milestone
    try {
      await fetch(`${API}/feedback/stay-survey/dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ milestone }),
      });
    } catch {
      /* silent */
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (selected.length === 0 && !freeText.trim()) {
      toast.error("Pick at least one or drop a quick note");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/feedback/stay-survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ milestone, tags: selected, free_text: freeText }),
      });
      if (r.ok) {
        toast.success("Thanks — means a lot.");
        setOpen(false);
      } else {
        toast.error("Couldn't save. Try again?");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const titleByMilestone = {
    30: "30 days of Paladin. Appreciated.",
    90: "90 days in. That's real commitment.",
    180: "6 months strong. You're elite.",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleClose}
      data-testid="stay-survey-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-lg bg-surface border border-border rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-pink-500/15 flex items-center justify-center flex-shrink-0 mt-1">
              <Heart className="w-5 h-5 text-pink-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-pink-400 font-bold">
                A quick one from us
              </p>
              <h3 className="text-xl font-bold text-white mt-1">
                {titleByMilestone[milestone] || "Thanks for sticking around"}
              </h3>
              <p className="text-sm text-text-secondary mt-2">
                What's working for you? (pick as many as you want)
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/5 rounded-lg"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-text-muted" />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {options &&
            Object.entries(options).map(([key, label]) => {
              const on = selected.includes(key);
              return (
                <button
                  key={key}
                  onClick={() => toggle(key)}
                  className={`px-3 py-2 rounded-full text-sm border transition-all ${
                    on
                      ? "bg-pink-500/15 border-pink-500/40 text-white"
                      : "bg-surface border-border text-text-secondary hover:border-pink-500/20"
                  }`}
                  data-testid={`stay-tag-${key}`}
                >
                  {label}
                </button>
              );
            })}
        </div>

        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Anything specific you love? (optional)"
          className="w-full px-4 py-3 bg-[#0B0F1A] border border-border rounded-xl text-white placeholder-text-muted focus:border-pink-500/40 outline-none resize-none text-sm mb-5"
          data-testid="stay-free-text"
        />

        <div className="flex gap-2">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="flex-1 py-3 bg-surface border border-border rounded-lg hover:border-primary/50 transition-colors text-sm font-medium text-white disabled:opacity-50"
          >
            Later
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3 bg-gradient-to-r from-pink-600 to-pink-500 text-white rounded-lg font-semibold text-sm shadow-lg shadow-pink-500/20 disabled:opacity-60"
            data-testid="stay-submit"
          >
            {submitting ? "Saving…" : "Send it"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default StaySurveyModal;
